// src/utils/jobs/jobConstants.js
//
// Every "Jobs for You" enum lives HERE, not in src/utils/constant.js. That
// shared file is a 600-line default-export object touched by many features,
// and its edit for this feature is deliberately kept to the five DB_NAME keys
// plus JOBS_SCREEN. Anything jobs-only belongs in this module.
//
// The values are NOT free-form: they are the client half of the frozen
// canonical-query contract in functions/jobs/queryFingerprint.js. The server
// re-normalises everything it receives, so a mismatch here does not corrupt
// the cache - it silently searches for the default instead, which is much
// harder to notice. Keep these in step with that file:
//   workMode        -> WORK_MODE_VALUES  ["any","hybrid","onsite","remote"]
//   jobType         -> JOB_TYPE_VALUES   ["any","contract","fulltime",
//                                         "internship","parttime","temporary"]
//   datePostedHours -> DATE_POSTED_HOURS [0, 24, 72, 168, 720]
//   sites           -> JOB_SITE_ALLOWLIST, capped at 5
//
// Option arrays are { label, value } so they drop straight into a Bootstrap
// `.form-select` (react-select is deliberately avoided - it is themed through
// hashed emotion selectors in style.css). Colours are Bootstrap CSS custom
// properties, never hex literals and never src/Styles/colors.js (dead code).

// ---------------------------------------------------------------------------
// Application tracker
// ---------------------------------------------------------------------------

// Doubles as the `status` enum of saved-jobs/{uid}/items/{jobId}.
export const JOB_STATUS = {
  SAVED: "saved",
  APPLIED: "applied",
  INTERVIEW: "interview",
  REJECTED: "rejected",
  OFFER: "offer",
};

export const DEFAULT_JOB_STATUS = JOB_STATUS.SAVED;

// Ordered as the funnel reads, which is also the tab order in the tracker.
export const JOB_STATUS_OPTIONS = [
  { label: "Saved", value: JOB_STATUS.SAVED, colorVar: "var(--bs-secondary)" },
  {
    label: "Applied",
    value: JOB_STATUS.APPLIED,
    colorVar: "var(--bs-primary)",
  },
  {
    label: "Interview",
    value: JOB_STATUS.INTERVIEW,
    colorVar: "var(--bs-info)",
  },
  { label: "Offer", value: JOB_STATUS.OFFER, colorVar: "var(--bs-success)" },
  {
    label: "Rejected",
    value: JOB_STATUS.REJECTED,
    colorVar: "var(--bs-danger)",
  },
];

export const JOB_STATUS_VALUES = JOB_STATUS_OPTIONS.map((item) => item.value);

// The tracker filters by status CLIENT-SIDE (a .where + .orderBy would need a
// composite index, and this repo has no firestore.indexes.json), so "all" is a
// UI-only value that must never reach Firestore.
export const JOB_STATUS_FILTER_ALL = "all";

export const JOB_STATUS_FILTER_OPTIONS = [
  {
    label: "All",
    value: JOB_STATUS_FILTER_ALL,
    colorVar: "var(--bs-secondary)",
  },
  ...JOB_STATUS_OPTIONS,
];

// statusHistory is append-only and capped, per the saved-jobs schema.
export const MAX_JOB_STATUS_HISTORY = 20;

// ---------------------------------------------------------------------------
// Search filters
// ---------------------------------------------------------------------------

export const WORK_MODE = {
  ANY: "any",
  REMOTE: "remote",
  HYBRID: "hybrid",
  ONSITE: "onsite",
};

export const WORK_MODE_OPTIONS = [
  { label: "Any work mode", value: WORK_MODE.ANY },
  { label: "Remote", value: WORK_MODE.REMOTE },
  { label: "Hybrid", value: WORK_MODE.HYBRID },
  { label: "On-site", value: WORK_MODE.ONSITE },
];

// A NormalizedJob's own workMode can additionally be "unknown" (JobSpy gives
// no hybrid signal for most boards), which is never a filter value.
export const JOB_WORK_MODE_LABELS = {
  remote: "Remote",
  hybrid: "Hybrid",
  onsite: "On-site",
  unknown: "Not specified",
};

export const JOB_TYPE = {
  ANY: "any",
  FULLTIME: "fulltime",
  PARTTIME: "parttime",
  CONTRACT: "contract",
  INTERNSHIP: "internship",
  TEMPORARY: "temporary",
};

export const JOB_TYPE_OPTIONS = [
  { label: "Any job type", value: JOB_TYPE.ANY },
  { label: "Full-time", value: JOB_TYPE.FULLTIME },
  { label: "Part-time", value: JOB_TYPE.PARTTIME },
  { label: "Contract", value: JOB_TYPE.CONTRACT },
  { label: "Internship", value: JOB_TYPE.INTERNSHIP },
  { label: "Temporary", value: JOB_TYPE.TEMPORARY },
];

export const JOB_TYPE_LABELS = {
  fulltime: "Full-time",
  parttime: "Part-time",
  contract: "Contract",
  internship: "Internship",
  temporary: "Temporary",
  unknown: "Not specified",
};

// 0 is "Any time" and is therefore the WIDEST bucket, not the narrowest.
// Only these five numbers survive server-side canonicalisation.
export const DATE_POSTED_HOURS = {
  ANY: 0,
  DAY: 24,
  THREE_DAYS: 72,
  WEEK: 168,
  MONTH: 720,
};

export const DATE_POSTED_OPTIONS = [
  { label: "Any time", value: DATE_POSTED_HOURS.ANY },
  { label: "Last 24 hours", value: DATE_POSTED_HOURS.DAY },
  { label: "Last 3 days", value: DATE_POSTED_HOURS.THREE_DAYS },
  { label: "Last week", value: DATE_POSTED_HOURS.WEEK },
  { label: "Last 30 days", value: DATE_POSTED_HOURS.MONTH },
];

// Value strings are JobSpy site keys - note the underscore in "zip_recruiter".
// Three boards is the default because the Python side runs a
// ThreadPoolExecutor(max_workers=3); asking for five serialises two of them
// past their per-site deadline.
export const JOB_SITE_OPTIONS = [
  { label: "Google Jobs", value: "google" },
  { label: "Indeed", value: "indeed" },
  { label: "LinkedIn", value: "linkedin" },
  { label: "Glassdoor", value: "glassdoor" },
  { label: "ZipRecruiter", value: "zip_recruiter" },
];

export const DEFAULT_JOB_SITES = ["google", "indeed", "linkedin"];

// Display names for NormalizedJob.source, which is NOT the same set as
// JOB_SITE_OPTIONS above.
//
// JOB_SITE_OPTIONS is the list of boards the JobSpy scraper can be pointed at,
// and it drives the site checkboxes in the search filters. The public-api
// provider reads five further boards that are not scraper targets and must not
// appear as selectable sites - so their labels live here instead. Adding them
// to JOB_SITE_OPTIONS would have offered the user filter checkboxes that the
// canonical query's JOB_SITE_ALLOWLIST silently discards.
//
// A source with no entry falls back to its raw value, which is why this only
// needs the ones whose raw form reads badly ("remoteok" -> "Remote OK").
export const JOB_SOURCE_LABELS = {
  ...JOB_SITE_OPTIONS.reduce((accumulator, option) => {
    accumulator[option.value] = option.label;
    return accumulator;
  }, {}),
  remotive: "Remotive",
  jobicy: "Jobicy",
  himalayas: "Himalayas",
  remoteok: "Remote OK",
  arbeitnow: "Arbeitnow",
};

// Server-side cap. Sending more is not an error, the extras are dropped.
export const MAX_JOB_SITES = 5;

export const DEFAULT_COUNTRY_INDEED = "usa";

export const JOB_RESULTS_WANTED = {
  MIN: 10,
  MAX: 60,
  DEFAULT: 20,
};

// Mirrors the server's input caps so the UI can stop a user before a 400.
export const MAX_JOB_TITLE_CHARS = 200;
export const MAX_JOB_LOCATION_CHARS = 200;

// The shape useJobSearch starts from and resets to.
export const DEFAULT_JOB_SEARCH_FILTERS = {
  title: "",
  location: "",
  workMode: WORK_MODE.ANY,
  datePostedHours: DATE_POSTED_HOURS.WEEK,
  jobType: JOB_TYPE.ANY,
  sites: DEFAULT_JOB_SITES,
  countryIndeed: DEFAULT_COUNTRY_INDEED,
  resultsWanted: JOB_RESULTS_WANTED.DEFAULT,
};

// ---------------------------------------------------------------------------
// Provider status (jobsSearch always returns 200 with one of these)
// ---------------------------------------------------------------------------

export const JOB_SEARCH_STATUS = {
  FRESH: "fresh",
  CACHED: "cached",
  DEGRADED: "degraded",
  FALLBACK: "fallback",
};

// Indeed 403s and Google CAPTCHAs from Cloud Functions egress, so "fallback"
// is the expected common case. Copy must read as intentional, not as an error.
export const JOB_SEARCH_STATUS_LABELS = {
  fresh: "Fresh results",
  cached: "Recent results",
  degraded: "Partial results",
  fallback: "Search links only",
};

export const PER_SITE_STATUS_LABELS = {
  ok: "Returned results",
  empty: "No matches",
  blocked: "Did not respond",
  timeout: "Timed out",
  error: "Unavailable",
};

// ---------------------------------------------------------------------------
// Paging and AI batching
// ---------------------------------------------------------------------------

// Six cards per page is also exactly one AI batch, so the first page of
// results costs one Gemini call and paging costs nothing until asked for.
export const JOBS_PAGE_SIZE = 6;

// What the client sends in one fetchJobMatchScores call.
export const JOBS_AI_BATCH_SIZE = 6;

// What the endpoint accepts in one call. Never send more than this.
export const MAX_JOB_MATCH_BATCH = 8;

// Per-job description slice sent with a match-score stub.
export const JOB_MATCH_SNIPPET_CHARS = 900;

// Description length frozen into a saved-jobs snapshot (job-search-cache
// expires after 6h, a saved job must outlive it).
export const SAVED_JOB_DESCRIPTION_CHARS = 4000;

// Server-side daily ceiling on batched AI scoring calls, mirrored here only so
// the UI can explain a usedAi:false response rather than looking broken.
export const MAX_JOBS_AI_CALLS_PER_DAY = 8;

// Heuristic scores are deterministic keyword overlap, NOT an AI match. The
// label is a constant so no component can accidentally call it an AI score.
export const HEURISTIC_SCORE_LABEL = "Keyword fit";
export const AI_SCORE_LABEL = "AI match";

export default {
  JOB_STATUS,
  DEFAULT_JOB_STATUS,
  JOB_STATUS_OPTIONS,
  JOB_STATUS_VALUES,
  JOB_STATUS_FILTER_ALL,
  JOB_STATUS_FILTER_OPTIONS,
  MAX_JOB_STATUS_HISTORY,
  WORK_MODE,
  WORK_MODE_OPTIONS,
  JOB_WORK_MODE_LABELS,
  JOB_TYPE,
  JOB_TYPE_OPTIONS,
  JOB_TYPE_LABELS,
  DATE_POSTED_HOURS,
  DATE_POSTED_OPTIONS,
  JOB_SITE_OPTIONS,
  JOB_SOURCE_LABELS,
  DEFAULT_JOB_SITES,
  MAX_JOB_SITES,
  DEFAULT_COUNTRY_INDEED,
  JOB_RESULTS_WANTED,
  MAX_JOB_TITLE_CHARS,
  MAX_JOB_LOCATION_CHARS,
  DEFAULT_JOB_SEARCH_FILTERS,
  JOB_SEARCH_STATUS,
  JOB_SEARCH_STATUS_LABELS,
  PER_SITE_STATUS_LABELS,
  JOBS_PAGE_SIZE,
  JOBS_AI_BATCH_SIZE,
  MAX_JOB_MATCH_BATCH,
  JOB_MATCH_SNIPPET_CHARS,
  SAVED_JOB_DESCRIPTION_CHARS,
  MAX_JOBS_AI_CALLS_PER_DAY,
  HEURISTIC_SCORE_LABEL,
  AI_SCORE_LABEL,
};
